import { useContext, useId, type ComponentPropsWithRef, type ReactElement } from 'react'

import { cn } from './cn'
import { FieldError } from './FieldError'
import { FieldLabelContext } from './field-label'
import type { IconComponent } from './icons'

interface TextFieldOwnProps {
  /** Left out (or empty) inside a `FormRow`, whose label then names the field. */
  readonly label?: string
  /** Follows the row's label when a `FormRow` holds several fields ("Duration" → "Duration value"). */
  readonly labelSuffix?: string
  /** Keeps the label for assistive technology only, as on a search bar. */
  readonly hideLabel?: boolean
  /** A leading icon, such as `Search` on a search bar. Left out, the field is plain. */
  readonly icon?: IconComponent
  /** `compact` is 33px, to sit beside a small button; `small` is the archetype's 40px. */
  readonly size?: 'small' | 'compact'
  /** The error message; when set, the stroke turns error and the field names the message. */
  readonly error?: string
  /**
   * The viewer may not change it: read-only, still focusable and readable, with the read-only fill
   * a locked Select has. Leave it off a field that is read-only only because a chooser fills it.
   */
  readonly isLocked?: boolean
  readonly className?: string
}

type InputProps = { readonly multiline?: false } & Omit<
  ComponentPropsWithRef<'input'>,
  'size' | 'className'
>
type TextareaProps = { readonly multiline: true } & Omit<
  ComponentPropsWithRef<'textarea'>,
  'className'
>

export type TextFieldProps = TextFieldOwnProps & (InputProps | TextareaProps)

/**
 * The archetype's text field: 5px rounded, a `text-light` stroke at rest and a primary stroke
 * and ring on focus. Plain unless given an icon, as a search bar is (`icon={Search}`). The label
 * sits above as a caption.
 */
export function TextField(props: TextFieldProps) {
  const generatedId = useId()
  const errorId = useId()
  const suffixId = useId()
  const { label, labelSuffix, hideLabel = false, icon: Icon, size = 'small', error, isLocked = false, className } = props
  const id = props.id ?? generatedId
  const rowLabelId = useContext(FieldLabelContext)
  const hasLabel = label !== undefined && label !== ''
  // Its own name first; else the row's label, then its suffix.
  const named = hasLabel || props['aria-label'] !== undefined
  const suffixed = !named && rowLabelId !== null && labelSuffix !== undefined && labelSuffix !== ''
  const rowLabelledBy = rowLabelId === null ? undefined : suffixed ? `${rowLabelId} ${suffixId}` : rowLabelId
  const labelledBy = props['aria-labelledby'] ?? (named ? undefined : rowLabelledBy)
  const multiline = props.multiline === true

  const fieldClasses = cn(
    'w-full rounded border text-sm text-text-primary placeholder:text-text-light focus:border-primary-main focus:outline-hidden focus:ring-2 focus:ring-primary-main',
    isLocked ? 'cursor-default bg-background-contrast-sm' : 'bg-background',
    error === undefined ? 'border-text-light' : 'border-error-main',
    Icon === undefined ? 'px-4' : 'pl-10 pr-4',
    multiline ? 'py-2' : size === 'compact' ? 'h-[33px]' : 'h-10',
  )
  const describedBy =
    error === undefined
      ? props['aria-describedby']
      : [props['aria-describedby'], errorId].filter(Boolean).join(' ')

  let field: ReactElement
  if (props.multiline === true) {
    const {
      label: _label,
      labelSuffix: _labelSuffix,
      hideLabel: _hideLabel,
      icon: _icon,
      size: _size,
      error: _error,
      isLocked: _isLocked,
      className: _className,
      multiline: _multiline,
      ...rest
    } = props
    field = (
      <textarea
        rows={3}
        {...rest}
        readOnly={isLocked || rest.readOnly}
        id={id}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        className={fieldClasses}
      />
    )
  } else {
    const {
      label: _label,
      labelSuffix: _labelSuffix,
      hideLabel: _hideLabel,
      icon: _icon,
      size: _size,
      error: _error,
      isLocked: _isLocked,
      className: _className,
      multiline: _multiline,
      ...rest
    } = props
    field = (
      <input
        type="text"
        {...rest}
        readOnly={isLocked || rest.readOnly}
        id={id}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        className={fieldClasses}
      />
    )
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {hasLabel && (
        <label
          htmlFor={id}
          className={hideLabel ? 'sr-only' : 'text-xs font-medium text-text-secondary'}
        >
          {label}
        </label>
      )}
      {suffixed && (
        <span id={suffixId} className="sr-only">
          {labelSuffix}
        </span>
      )}
      <div className="relative">
        {Icon !== undefined && (
          <Icon
            size={18}
            className={cn(
              'pointer-events-none absolute left-3 text-text-light',
              multiline ? 'top-3' : 'top-1/2 -translate-y-1/2',
            )}
          />
        )}
        {field}
      </div>
      {error !== undefined && <FieldError id={errorId} message={error} />}
    </div>
  )
}
